import { Component, OnInit, ElementRef, ViewChild, forwardRef, Input, SimpleChanges, OnChanges, HostListener, HostBinding, Output, EventEmitter, Inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { isNil, set } from 'lodash-es';
import { Styles } from './models/style';
import { SelectorComponent } from './components/selector.component';
import { ESelectorType } from './components/selector.component';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { TransloadOrders_ShellService } from './TransloadOrders.shell.service';
import { TransloadOrders_OperationService } from './TransloadOrders.operation.service';
import { TransloadOrders_DatasourceService } from './TransloadOrders.datasource.index';
import { TransloadOrders_FlowService } from './TransloadOrders.flow.index';
import { TransloadOrders_ReportService } from './TransloadOrders.report.index';
import { TransloadOrders_LocalizationService } from './TransloadOrders.localization.service';
import { TransloadOrders_transload_composites_by_transload_id_dd_ComponentContextService } from './TransloadOrders.transload_composites_by_transload_id_dd.component.context.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { ApplicationType, ComponentType } from './app-context.service';
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './TransloadOrders.frontend.types'
import { $frontendTypes as $types} from './TransloadOrders.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';

@Component({
  standalone: true,
  imports: [
    SharedModule,
  ],
  selector: 'TransloadOrders-transload_composites_by_transload_id_dd_multi',
  templateUrl: './TransloadOrders.transload_composites_by_transload_id_dd_multi.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TransloadOrders_transload_composites_by_transload_id_dd_multiComponent),
      multi: true
    }
  ]
})
export class TransloadOrders_transload_composites_by_transload_id_dd_multiComponent extends BaseComponent implements OnInit, ControlValueAccessor, OnChanges {

  @Input() type?: ESelectorType;
  _disabled = false;
  @Input() get disabled() { 
      return this._disabled 
      || this.$hasMissingRequiredInParams
      ; }
  set disabled(val: any) { this._disabled = val; }

  @Input() placeholder = '';
  @Input() styles: Styles;
  @Input() tooltip = '';

  @Output() displayTextChange: EventEmitter<string> = new EventEmitter();

  @ViewChild(SelectorComponent) selector: SelectorComponent;

  @HostBinding('tabIndex') get tabIndex() { return -1; }
  @HostListener('focus')
  focus() {
    this.selector?.focus();
  }

  get isMulti() {
    return true;
  }

  get isSingle() {
    return false;
  }


  inParams: { transload_id: number, excluded_shipment_id?: number } = { transload_id: null, excluded_shipment_id: null };
  @Input('transload_id') set $inParams_transload_id(value: any) {
    this.inParams['transload_id'] = value;
  }
  get $inParams_transload_id(): any {
    return this.inParams['transload_id'] ;
  }
  @Input('excluded_shipment_id') set $inParams_excluded_shipment_id(value: any) {
    this.inParams['excluded_shipment_id'] = value;
  }
  get $inParams_excluded_shipment_id(): any {
    return this.inParams['excluded_shipment_id'] ;
  }

  fullTextSearch: string;

  constructor(
    private $utils: UtilsService,
    private $settings: SettingsValuesService,
    private $shell: TransloadOrders_ShellService,
    private $datasources: TransloadOrders_DatasourceService,
    private $flows: TransloadOrders_FlowService,
    private $reports: TransloadOrders_ReportService,
    private $localization: TransloadOrders_LocalizationService,
    private $operations: TransloadOrders_OperationService,
    private $logger: CleanupLoggerService,
    private $context: TransloadOrders_transload_composites_by_transload_id_dd_ComponentContextService,
    private _elementRef: ElementRef
    ) {
      super();
  }

  ngOnInit(): void {
    this.$checkRequiredInParams();
    if (!this.$hasMissingRequiredInParams) {
      this.$init();
    } else {
      this.$initEmpty();
    }
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$checkRequiredInParams();
      if(!this.$hasMissingRequiredInParams) {
        this.$init();
      } else {
        this.$initEmpty();
      }
    }
  }

  $missingRequiredInParams = [];
  get $hasMissingRequiredInParams(): boolean {
    return !!this.$missingRequiredInParams.length;
  }
  
  $checkRequiredInParams() {
    this.$missingRequiredInParams = [];
      if(isNil(this.inParams.transload_id)) {
        this.$missingRequiredInParams.push('transload_id');
      }
  }

  async $init() {
    this.selector?.init();
  }

  onKeyDown($event: KeyboardEvent) {
    //TODO:KeyDown    
  }

  //ControlValueAccessor
  _innerValue: any;

  onChange: any = () => { };
  onTouch: any = () => { };

  get value(): { LicensePlateId: number, Shipmentid: number,  } | { LicensePlateId: number, Shipmentid: number,  }[] {
    return this._innerValue;
  }

  set value(val:  { LicensePlateId: number, Shipmentid: number,  } | { LicensePlateId: number, Shipmentid: number,  }[]) {
    if (this._innerValue !== val) {
      this._innerValue = val;
      this.onChange(val);
      this.onTouch(val);
    }
  }

  writeValue(value: any): void {
    this._innerValue = value;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  displayWithFn(value: any): Promise<{displayText:string, selected :Array<{ key: any; name: string; disabled?: boolean; selected?: boolean;}>}> {
    const promise = new Promise<{displayText:string, selected :Array<{ key: any; name: string; disabled?: boolean; selected?: boolean;}>}>((resolve, reject) => {

      const $selector = this;
      const $utils = this.$utils;

      this.fullTextSearch = null;
      const $fullTextSearch = null;
      this.$datasources.TransloadOrders.ds_transload_composites_by_transload_id.getByKeys({
        $keys: value,
        transload_id:  $selector.inParams.transload_id ,
        excluded_shipment_id:  $selector.inParams.excluded_shipment_id ,
      }).then(result => {
        const selected = value.map(k => {
          const found = result.result
            .find(i => i.LicensePlateId === k);
          const e: { LicensePlateId?: number, Shipmentid?: number, Shipment?: { Id?: number, LookupCode?: string }, LicensePlate?: { TypeId?: number } } = found 
            ?? { 
              LicensePlateId : k
            };

          const $option = { entity: e };
          const $entity = e;
          const $resultOption = { key: null, name: found ? $option.entity.Shipment.LookupCode : 'No data to display', disabled: false, selected:true };
          $resultOption.key = {};
          set($resultOption.key, 'LicensePlateId',$option.entity.LicensePlateId);
          set($resultOption.key, 'Shipmentid',$option.entity.Shipmentid);
          return $resultOption;
        });
        const displayText = value.map(k => {
          const found = result.result
            .find(i => i.LicensePlateId === k);
          if (found) {
            const $option = { entity: found };
            const $entity = found;
            return $option.entity.Shipment.LookupCode;
          } else {
            return 'No data to display';
          }
        })
        .join(
           ', '
        );
        this.displayTextChange.emit(displayText);
        resolve({displayText, selected});
      });
    });
    return promise;
  }

  optionsFn(filterText: string): Promise<{ list: Array<{ key: any; name: string; disabled?: boolean; }>, totalCount?: number, top?: number }> {

    const promise = new Promise<{ list: Array<{ key: any; name: string; disabled?: boolean; }>, totalCount?: number, top?: number }>((resolve, reject) => {

      const $selector = this;
      const $utils = this.$utils;

      this.fullTextSearch = filterText;
      const $fullTextSearch = filterText;
      this.$datasources.TransloadOrders.ds_transload_composites_by_transload_id.getList({
        $top: 25,
        $skip: 0,
        transload_id:  $selector.inParams.transload_id ,
        excluded_shipment_id:  $selector.inParams.excluded_shipment_id ,
      }).then(result => {
        resolve({
          list: result.result.map(item => {
            const $option = { entity: item };
            const $entity = item;
            const $resultOption = { key: null, name: $option.entity.Shipment.LookupCode, disabled: null };
            $resultOption.key = {};
            set($resultOption.key, 'LicensePlateId',$option.entity.LicensePlateId);
            set($resultOption.key, 'Shipmentid',$option.entity.Shipmentid);
            return $resultOption;
          }),
          totalCount: result.totalCount,
          top: 25
        });
      });

    });

    return promise;

  }

}
